import React, { useState, useMemo } from "react";
import { startOfMonth, endOfMonth } from "date-fns";
import {
  FaDollarSign,
  FaUserCheck,
  FaUserTimes,
  FaChartLine,
} from "react-icons/fa";

import { useDashboardMetrics } from "../hooks/useDashboardMetrics";
import KPICard                 from "./Dashboard/KPICard";
import DashboardFilters        from "./Dashboard/DashboardFilters";
import MRRGrowthChart          from "./Dashboard/MRRGrowthChart";
import SubscriptionMomentumChart from "./Dashboard/SubscriptionMomentumChart";
import PlanDistributionChart  from "./Dashboard/PlanDistributionChart";
import styles from "../stripe-dashboard.module.css";

const formatCurrency = v =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v ?? 0);
const formatNumber = v => new Intl.NumberFormat("pt-BR").format(v ?? 0);
const formatPct    = v => `${(v ?? 0).toFixed(1)}%`;

const Dashboard = ({ token }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  const dateRange = useMemo(
    () => ({
      startDate: startOfMonth(selectedMonth),
      endDate:   endOfMonth(selectedMonth),
    }),
    [selectedMonth]
  );

  const { data, loading, error } = useDashboardMetrics(dateRange, { token });

  if (loading) {
    return (
      <div className={styles.loadingWrap} style={{ minHeight: 200 }}>
        <div className={styles.spinner} />
        <span>Carregando métricas...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorWrap}>
        <i className="fas fa-exclamation-circle" /> {error}
      </div>
    );
  }

  return (
    <div>
      <DashboardFilters selectedMonth={selectedMonth} onMonthChange={setSelectedMonth} />

      {data && (
        <>
          <div className={styles.kpiGrid}>
            <KPICard
              title="MRR"
              value={data.kpis.mrr}
              format={formatCurrency}
              icon={<FaDollarSign size={26} />}
                delta={data.kpis.mrrDeltaPercent}
                tooltipText="Receita paga no mês selecionado (MRR proxy). Variação vs mês anterior."
            />
            <KPICard
              title="Assinantes Ativos"
              value={data.kpis.activeSubscribers}
              format={formatNumber}
              icon={<FaUserCheck size={26} />}
                delta={data.kpis.activeSubscribersDeltaPercent}
                tooltipText="Pagantes no mês selecionado. Variação vs mês anterior."
            />
            <KPICard
              title="Churn Rate"
              value={data.kpis.churnRate}
              format={formatPct}
              icon={<FaUserTimes size={26} />}
                delta={data.kpis.churnRateDeltaPercent}
                tooltipText="Cancelamentos com efetivação no mês, dividido por pagantes do mês anterior."
            />
            <KPICard
              title="Novos Assinantes"
              value={data.kpis.newSubscribers}
              format={formatNumber}
              icon={<FaChartLine size={26} />}
                delta={data.kpis.newSubscribersDeltaPercent}
                tooltipText="Novos assinantes iniciados no mês selecionado. Variação vs mês anterior."
            />
          </div>

              {Array.isArray(data.kpis.activeSubscribersDetails) &&
                data.kpis.activeSubscribersDetails.length > 0 && (
                  <div style={{ marginTop: 18 }}>
                    <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>
                      Assinantes Ativos (detalhes)
                      <span style={{ color: "#64748b", fontWeight: 700 }}>
                        {" "}
                        · mostrando {data.kpis.activeSubscribersDetails.length} de{" "}
                        {data.kpis.activeSubscribers}
                      </span>
                    </div>
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 12,
                        padding: 12,
                        boxShadow: "0 1px 4px rgba(15, 23, 42, 0.05)",
                        maxHeight: 260,
                        overflow: "auto"
                      }}
                    >
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                          <tr style={{ textAlign: "left" }}>
                            <th style={{ padding: "8px 6px", color: "#64748b" }}>Usuário</th>
                            <th style={{ padding: "8px 6px", color: "#64748b" }}>Email</th>
                            <th style={{ padding: "8px 6px", color: "#64748b" }}>Plano</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.kpis.activeSubscribersDetails.map(s => (
                            <tr key={s.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                              <td style={{ padding: "8px 6px", fontWeight: 700, color: "#0f172a" }}>
                                {s.username || "—"}
                              </td>
                              <td style={{ padding: "8px 6px", color: "#334155" }}>
                                {s.email || "—"}
                              </td>
                              <td style={{ padding: "8px 6px", color: "#334155" }}>
                                {s.plan || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

          <div className={styles.chartsGrid}>
            <MRRGrowthChart data={data.charts.mrrGrowth} />
            <PlanDistributionChart data={data.charts.planDistribution} />
            <SubscriptionMomentumChart data={data.charts.subscriptionMomentum} />
          </div>
        </>
      )}
    </div>
  );
};

export { Dashboard };
